/**
 * Mission Control — the operational heartbeat of Orkexra (M6).
 *
 * - Month / Week / Day / Agenda tabs reslice a single dataset (no refetch).
 * - Ops dashboard rail (7 counters) sits above the canvas.
 * - Campaign timeline strip lives below the canvas.
 * - Drag-and-drop scheduling uses `@dnd-kit/core` — the only place in the
 *   app that mounts DnD sensors.
 * - Time-zone toggle (workspace/local/UTC) reflows client-side only.
 */
import { useMemo, useState } from "react";
import { DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

import { useCurrentWorkspace } from "@/entities/workspace";
import {
  getCalendar, getCalendarConflicts, getCampaignTimeline, getDashboard,
  patchSchedule, type CalendarEvent, type ChannelKey, type Dashboard,
} from "@/entities/mission-control";
import { invalidateCampaignLifecycle } from "@/entities/campaign-run";
import { Button } from "@/shared/ui/button";

import { AssetDrawer } from "./AssetDrawer";
import { CalendarCell } from "./CalendarCell";
import { CampaignTimeline } from "./CampaignTimeline";
import { ConflictPanel } from "./ConflictPanel";
import { EventChip } from "./EventChip";
import { OpsRail } from "./OpsRail";
import { QueueHealthPanel } from "./QueueHealthPanel";
import {
  buildDayHours, buildMonthGrid, buildWeekGrid, DAY_SHORT, formatInFrame,
  hourForIso, isoForDropSlot, MONTH_LABELS, startOfMonth, ymdForIso, ymdKey,
  type TzMode, type ViewMode,
} from "./calendar-math";

export default function MissionControlPage(): JSX.Element {
  const workspace = useCurrentWorkspace();
  const qc = useQueryClient();
  const [anchor, setAnchor] = useState(new Date());
  const [view, setView] = useState<ViewMode>("month");
  const [tz, setTz] = useState<TzMode>("workspace");
  const [openEvent, setOpenEvent] = useState<CalendarEvent | null>(null);

  const workspaceId = workspace?.id;
  const workspaceTz = workspace?.settings?.time_zone ?? "UTC";

  const rangeFrom = useMemo(() => {
    const c = new Date(startOfMonth(anchor));
    c.setDate(c.getDate() - 14);
    return c.toISOString();
  }, [anchor]);
  const rangeTo = useMemo(() => {
    const c = new Date(startOfMonth(anchor));
    c.setMonth(c.getMonth() + 2);
    return c.toISOString();
  }, [anchor]);

  const queries = useQueries({
    queries: [
      { queryKey: ["mc", "calendar", workspaceId, rangeFrom, rangeTo], queryFn: () => getCalendar(workspaceId!, rangeFrom, rangeTo), enabled: !!workspaceId },
      { queryKey: ["mc", "dashboard", workspaceId], queryFn: () => getDashboard(workspaceId!), refetchInterval: 6_000, enabled: !!workspaceId },
      { queryKey: ["mc", "conflicts", workspaceId], queryFn: () => getCalendarConflicts(workspaceId!), refetchInterval: 10_000, enabled: !!workspaceId },
      { queryKey: ["mc", "timeline", workspaceId], queryFn: () => getCampaignTimeline(workspaceId!), enabled: !!workspaceId },
    ],
  });
  const [calQ, dashQ, confQ, timelineQ] = queries;
  const events: CalendarEvent[] = (calQ.data as CalendarEvent[] | undefined) ?? [];
  const dashboard = dashQ.data as Dashboard | undefined;
  const conflicts = (confQ.data ?? []) as Awaited<ReturnType<typeof getCalendarConflicts>>;
  const timeline = timelineQ.data ?? [];

  const patch = useMutation({
    mutationFn: (v: { id: string; scheduled_at?: string; channel?: ChannelKey }) =>
      patchSchedule(workspaceId!, v.id, { scheduled_at: v.scheduled_at, channel: v.channel }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: ["mc", "calendar"] });
      const prev = qc.getQueryData<CalendarEvent[]>(["mc", "calendar", workspaceId, rangeFrom, rangeTo]);
      if (prev) {
        qc.setQueryData<CalendarEvent[]>(
          ["mc", "calendar", workspaceId, rangeFrom, rangeTo],
          prev.map((e) => (e.id === v.id ? { ...e, scheduled_at: v.scheduled_at ?? e.scheduled_at, channel: v.channel ?? e.channel } : e)),
        );
      }
      return { prev };
    },
    onError: (err: Error, _v, ctx) => {
      if (ctx?.prev) {
        qc.setQueryData(["mc", "calendar", workspaceId, rangeFrom, rangeTo], ctx.prev);
      }
      toast.error(err.message);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mc"] });
      // Patch 34.2.1c · Schedule updates are lifecycle mutations —
      // refresh the canonical CampaignRun projection so Home, Vault,
      // and Publishing all catch up in lockstep.
      if (workspaceId) invalidateCampaignLifecycle(qc, workspaceId);
      toast.success("Schedule updated");
    },
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const onDragEnd = (evt: DragEndEvent): void => {
    if (!evt.over || !evt.active) return;
    const dropped = evt.active.data.current?.event as CalendarEvent | undefined;
    if (!dropped) return;
    const dropId = String(evt.over.id);
    // month cell → "cell:YYYY-MM-DD"; week/day cell → "cell:YYYY-MM-DD:HH"
    const parts = dropId.split(":");
    if (parts[0] !== "cell") return;
    const dateKey = parts[1];
    const hour = parts[2] ? parseInt(parts[2], 10) : hourForIso(dropped.scheduled_at, tz, workspaceTz);
    const newIso = isoForDropSlot(dateKey, hour, tz, workspaceTz);
    if (new Date(newIso).getTime() === new Date(dropped.scheduled_at).getTime()) return;
    patch.mutate({ id: dropped.id, scheduled_at: newIso });
  };

  const openDrawer = (e: CalendarEvent): void => setOpenEvent(e);

  const goPrev = (): void => {
    const c = new Date(anchor);
    if (view === "month") c.setMonth(c.getMonth() - 1);
    else if (view === "week") c.setDate(c.getDate() - 7);
    else c.setDate(c.getDate() - 1);
    setAnchor(c);
  };
  const goNext = (): void => {
    const c = new Date(anchor);
    if (view === "month") c.setMonth(c.getMonth() + 1);
    else if (view === "week") c.setDate(c.getDate() + 7);
    else c.setDate(c.getDate() + 1);
    setAnchor(c);
  };
  const goToday = (): void => setAnchor(new Date());

  const headerLabel =
    view === "month"
      ? `${MONTH_LABELS[anchor.getMonth()]} ${anchor.getFullYear()}`
      : view === "week"
      ? `Week of ${anchor.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`
      : anchor.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric", year: "numeric" });

  const formatFrame = (iso: string): string =>
    formatInFrame(iso, tz, workspaceTz, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });

  if (!workspace) {
    return (
      <div data-testid="mc-page" className="mx-auto max-w-3xl px-6 py-16 text-center">
        <div className="text-lg font-semibold">Select a workspace</div>
        <div className="mt-1 text-xs text-muted-foreground">Pick a workspace from the switcher to open Mission Control.</div>
      </div>
    );
  }

  return (
    <div data-testid="mc-page" className="mx-auto max-w-7xl space-y-4 px-6 py-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Mission Control · {workspace.slug}
          </div>
          <h1 data-testid="mc-title" className="font-display text-3xl font-semibold tracking-tight">
            {headerLabel}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex overflow-hidden rounded-full border border-border/60">
            {(["month", "week", "day", "agenda"] as ViewMode[]).map((v) => (
              <button
                key={v}
                data-testid={`mc-view-${v}`}
                onClick={() => setView(v)}
                className={`px-3 py-1.5 text-xs font-medium capitalize transition ${
                  view === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
                }`}
              >
                {v}
              </button>
            ))}
          </div>
          <div className="flex items-center overflow-hidden rounded-full border border-border/60" data-testid="mc-tz-toggle">
            {(["workspace", "local", "utc"] as TzMode[]).map((t) => (
              <button
                key={t}
                onClick={() => setTz(t)}
                data-testid={`mc-tz-${t}`}
                className={`px-2.5 py-1.5 text-[10px] font-mono uppercase tracking-widest transition ${
                  tz === t ? "bg-secondary text-secondary-foreground" : "text-muted-foreground hover:bg-muted"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={goPrev} data-testid="mc-nav-prev" aria-label="Previous">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={goToday} data-testid="mc-nav-today">
              Today
            </Button>
            <Button variant="outline" size="sm" onClick={goNext} data-testid="mc-nav-next" aria-label="Next">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <OpsRail dashboard={dashboard as never} loading={dashQ.isLoading} />

      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
          <div className="min-w-0">
            {view === "month" ? <MonthView events={events} anchor={anchor} tz={tz} workspaceTz={workspaceTz} onOpen={openDrawer} /> : null}
            {view === "week" ? <WeekOrDayView events={events} anchor={anchor} tz={tz} workspaceTz={workspaceTz} onOpen={openDrawer} days={7} /> : null}
            {view === "day" ? <WeekOrDayView events={events} anchor={anchor} tz={tz} workspaceTz={workspaceTz} onOpen={openDrawer} days={1} /> : null}
            {view === "agenda" ? <AgendaView events={events} tz={tz} workspaceTz={workspaceTz} onOpen={openDrawer} /> : null}
          </div>
          <div className="min-w-0 space-y-4">
            <QueueHealthPanel workspaceId={workspace.id} />
            <ConflictPanel conflicts={conflicts as never[]} />
            <ActivityFeed dashboard={dashboard} />
          </div>
        </div>

        <CampaignTimeline timeline={timeline} onOpen={openDrawer} />
      </DndContext>

      <AssetDrawer
        workspaceId={workspace.id}
        event={openEvent}
        onClose={() => setOpenEvent(null)}
        formatFrame={formatFrame}
      />
    </div>
  );
}

/* ── view sub-components ─────────────────────────────────────────── */

function MonthView({
  events, anchor, tz, workspaceTz, onOpen,
}: {
  events: CalendarEvent[]; anchor: Date; tz: TzMode; workspaceTz: string;
  onOpen: (e: CalendarEvent) => void;
}): JSX.Element {
  const grid = buildMonthGrid(anchor);
  const byDay: Record<string, CalendarEvent[]> = {};
  for (const e of events) {
    const key = ymdForIso(e.scheduled_at, tz, workspaceTz);
    (byDay[key] ??= []).push(e);
  }
  const monthIdx = anchor.getMonth();
  const todayKey = ymdKey(new Date());
  return (
    <div className="rounded-lg border border-border/60 bg-card p-2 shadow-sm">
      <div className="grid grid-cols-7 gap-px pb-1 text-center font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        {DAY_SHORT.map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px overflow-hidden rounded-md bg-border/60">
        {grid.map((d) => {
          const key = ymdKey(d);
          const dayEvents = byDay[key] ?? [];
          return (
            <CalendarCell
              key={key}
              id={`cell:${key}`}
              testId={`mc-cell-${key}`}
              label={String(d.getDate())}
              isOtherMonth={d.getMonth() !== monthIdx}
              isToday={key === todayKey}
            >
              {dayEvents.slice(0, 4).map((e) => (
                <EventChip key={e.id} event={e} onOpen={onOpen} compact />
              ))}
              {dayEvents.length > 4 ? (
                <span className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">
                  +{dayEvents.length - 4} more
                </span>
              ) : null}
            </CalendarCell>
          );
        })}
      </div>
    </div>
  );
}

function WeekOrDayView({
  events, anchor, tz, workspaceTz, onOpen, days,
}: {
  events: CalendarEvent[]; anchor: Date; tz: TzMode; workspaceTz: string;
  onOpen: (e: CalendarEvent) => void; days: 1 | 7;
}): JSX.Element {
  const dates = days === 7 ? buildWeekGrid(anchor) : [anchor];
  const hours = buildDayHours();
  const bucketed: Record<string, Record<number, CalendarEvent[]>> = {};
  for (const e of events) {
    const dk = ymdForIso(e.scheduled_at, tz, workspaceTz);
    const h = hourForIso(e.scheduled_at, tz, workspaceTz);
    bucketed[dk] ??= {};
    (bucketed[dk][h] ??= []).push(e);
  }
  return (
    <div className="rounded-lg border border-border/60 bg-card p-2 shadow-sm">
      <div className={`grid ${days === 7 ? "grid-cols-[60px_repeat(7,1fr)]" : "grid-cols-[60px_1fr]"} gap-px bg-border/60`}>
        <div className="bg-background" />
        {dates.map((d) => (
          <div key={ymdKey(d)} className="bg-background px-2 py-1 text-center">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {d.toLocaleDateString(undefined, { weekday: "short" })}
            </div>
            <div className="text-sm font-semibold">{d.getDate()}</div>
          </div>
        ))}
        {hours.map((h) => (
          <>
            <div key={`h-${h}`} className="bg-background px-2 py-1 text-right font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {String(h).padStart(2, "0")}:00
            </div>
            {dates.map((d) => {
              const key = ymdKey(d);
              const dayHour = bucketed[key]?.[h] ?? [];
              return (
                <CalendarCell
                  key={`${key}-${h}`}
                  id={`cell:${key}:${h}`}
                  testId={`mc-cell-${key}-${String(h).padStart(2, "0")}`}
                >
                  {dayHour.map((e) => (
                    <EventChip
                      key={e.id}
                      event={e}
                      onOpen={onOpen}
                      timeLabel={new Date(e.scheduled_at).toLocaleTimeString(undefined, {
                        timeZone: tz === "utc" ? "UTC" : tz === "workspace" ? workspaceTz : undefined,
                        hour: "2-digit", minute: "2-digit",
                      })}
                    />
                  ))}
                </CalendarCell>
              );
            })}
          </>
        ))}
      </div>
    </div>
  );
}

function AgendaView({
  events, tz, workspaceTz, onOpen,
}: {
  events: CalendarEvent[]; tz: TzMode; workspaceTz: string;
  onOpen: (e: CalendarEvent) => void;
}): JSX.Element {
  const sorted = [...events].sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at));
  const byDay: Record<string, CalendarEvent[]> = {};
  for (const e of sorted) {
    const k = ymdForIso(e.scheduled_at, tz, workspaceTz);
    (byDay[k] ??= []).push(e);
  }
  const keys = Object.keys(byDay);
  return (
    <div className="rounded-lg border border-border/60 bg-card p-2 shadow-sm">
      {keys.length === 0 ? (
        <div className="px-4 py-8 text-center text-xs text-muted-foreground">
          Nothing scheduled in this window.
        </div>
      ) : (
        <ul className="divide-y divide-border/40">
          {keys.map((k) => (
            <li key={k} className="grid grid-cols-[120px_1fr] gap-4 px-3 py-3">
              <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                {new Date(k).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
              </div>
              <div className="space-y-2">
                {byDay[k].map((e) => (
                  <EventChip key={e.id} event={e} onOpen={onOpen} timeLabel={
                    new Date(e.scheduled_at).toLocaleTimeString(undefined, {
                      timeZone: tz === "utc" ? "UTC" : tz === "workspace" ? workspaceTz : undefined,
                      hour: "2-digit", minute: "2-digit",
                    })
                  } />
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ActivityFeed({ dashboard }: { dashboard: Dashboard | undefined }): JSX.Element {
  const feed = dashboard?.activity_feed ?? [];
  return (
    <div className="rounded-lg border border-border/60 bg-card p-3 shadow-sm">
      <div className="mb-2 text-sm font-semibold">Recent activity</div>
      {feed.length === 0 ? (
        <div className="rounded-md border border-dashed border-border/40 px-3 py-4 text-center text-[11px] text-muted-foreground">
          No activity yet.
        </div>
      ) : (
        <ul className="space-y-1.5">
          {feed.slice(0, 8).map((e) => (
            <li key={e.id} data-testid={`mc-activity-item-${e.id}`} className="flex items-baseline justify-between gap-2 text-[11px]">
              <span className="font-mono uppercase tracking-widest text-muted-foreground">
                {e.type.split(".").pop()}
              </span>
              <span className="flex-1 truncate">
                {typeof e.payload["to"] === "string" ? `→ ${e.payload["to"]}` : ""}
              </span>
              <span className="font-mono text-[9px] text-muted-foreground">
                {new Date(e.created_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
